import { useCallback, useState } from 'react';

// "Did this conditional section render on the last settled visit, and how tall
// was it?" — persisted in localStorage, read synchronously at mount.
//
// This is the Discount Prime dashboard-banner CLS pattern (WholesaleB2bBanner,
// commit 94cc94bf) generalised: a section whose visibility depends on fetched
// data must NEVER go null→pop-in on a page that has already painted, because
// that pushes everything below it and books a layout shift against the Built
// for Shopify CLS budget (< 0.1). Instead, while its data is pending:
//   - last visit showed it  → render a matched-height placeholder (hint.height)
//   - last visit hid it     → render nothing (nothing gets reserved OR shifted)
//   - first visit ever      → render nothing; the one-time appearance shift is
//     the accepted trade — it happens once per shop, not once per load.
//
// Usage:
//   const { hint, report } = useRenderHint('primeMoqDash:reviewBanner');
//   // while pending:
//   if (pending) return hint.visible ? <Placeholder minHeight={hint.height} /> : null;
//   // once settled, in an effect (post-commit, so the ref is measurable):
//   useEffect(() => { if (!pending) report(shown, el?.offsetHeight); }, [...]);
export function useRenderHint(storageId) {
  const key = `renderHint:${storageId}`;

  // Read once at mount — renders only ever need "what did the LAST settled
  // visit decide"; report() below keeps the stored value fresh for next time.
  const [hint] = useState(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return { visible: false, height: 0 };
      const parsed = JSON.parse(raw);
      return { visible: Boolean(parsed?.visible), height: Number(parsed?.height) || 0 };
    } catch (e) {
      return { visible: false, height: 0 };
    }
  });

  const report = useCallback(
    (visible, height) => {
      try {
        window.localStorage.setItem(key, JSON.stringify({ visible: Boolean(visible), height: Number(height) || 0 }));
      } catch (e) {}
    },
    [key]
  );

  return { hint, report };
}

export default useRenderHint;
